import { Injectable } from '@angular/core';
import { KpiChartData, KpiChartId, KpiKeys } from '@models';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class KpiChartDataStoreService {
  private readonly _kpiChartData = new BehaviorSubject<Partial<Record<KpiKeys, KpiChartData>>>({});
  private readonly _activeKpiKey = new BehaviorSubject<KpiKeys | null>(null);
  private readonly _loadingState = new BehaviorSubject<Partial<Record<KpiKeys, boolean>>>({});

  private readonly _displayValues = new BehaviorSubject<{
    kpiDisplayValue?: string | number;
    kpiDisplaySecondaryValue?: string;
    kpiDisplayUnit?: string;
  }>({});

  private readonly _toggleStates = new BehaviorSubject<
    Partial<Record<KpiKeys, Record<KpiChartId, KpiChartId>>>
  >({});

  // Public observables
  public readonly kpiChartData$: Observable<Partial<Record<KpiKeys, KpiChartData>>> =
    this._kpiChartData.asObservable();

  public readonly activeKpiKey$: Observable<KpiKeys | null> = this._activeKpiKey.asObservable();

  public readonly loadingState$: Observable<Partial<Record<KpiKeys, boolean>>> =
    this._loadingState.asObservable();

  public readonly displayValues$: Observable<{
    kpiDisplayValue?: string | number;
    kpiDisplaySecondaryValue?: string;
    kpiDisplayUnit?: string;
  }> = this._displayValues.asObservable();

  public readonly toggleStates$: Observable<
    Partial<Record<KpiKeys, Record<KpiChartId, KpiChartId>>>
  > = this._toggleStates.asObservable();

  // Derived observables
  public readonly activeKpiChartData$: Observable<KpiChartData | null> = combineLatest([
    this.kpiChartData$,
    this.activeKpiKey$
  ]).pipe(map(([chartData, activeKey]) => (activeKey ? chartData[activeKey] || null : null)));

  public readonly isActiveKpiLoading$: Observable<boolean> = combineLatest([
    this.loadingState$,
    this.activeKpiKey$
  ]).pipe(map(([loadingState, activeKey]) => (activeKey ? !!loadingState[activeKey] : false)));

  syncKpiChartData(data: Partial<Record<KpiKeys, KpiChartData>>): void {
    this._kpiChartData.next(data);
  }

  syncActiveKpiKey(kpiKey: KpiKeys | null): void {
    this._activeKpiKey.next(kpiKey);
  }

  syncLoadingState(kpiKey: KpiKeys, isLoading: boolean): void {
    const currentState = this._loadingState.value;
    this._loadingState.next({
      ...currentState,
      [kpiKey]: isLoading
    });
  }

  syncDisplayValues(values: {
    kpiDisplayValue?: string | number;
    kpiDisplaySecondaryValue?: string;
    kpiDisplayUnit?: string;
  }): void {
    this._displayValues.next(values);
  }

  setToggleState(kpiKey: KpiKeys, toggleChartId: KpiChartId, selectedChartId: KpiChartId): void {
    const currentStates = this._toggleStates.value;
    const kpiToggles = currentStates[kpiKey] || {};

    this._toggleStates.next({
      ...currentStates,
      [kpiKey]: {
        ...kpiToggles,
        [toggleChartId]: selectedChartId
      }
    });
  }

  getToggleState(kpiKey: KpiKeys, toggleChartId: KpiChartId): KpiChartId | null {
    const currentStates = this._toggleStates.value;
    return currentStates[kpiKey]?.[toggleChartId] || null;
  }

  getKpiToggleStates(kpiKey: KpiKeys): Partial<Record<KpiChartId, KpiChartId>> {
    return this._toggleStates.value[kpiKey] || {};
  }

  clearKpiToggleStates(kpiKey: KpiKeys): void {
    const currentStates = this._toggleStates.value;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [kpiKey]: _, ...remainingStates } = currentStates;
    this._toggleStates.next(remainingStates);
  }

  clearAllData(): void {
    this._kpiChartData.next({});
    this._loadingState.next({});
    this._activeKpiKey.next(null);
    this._toggleStates.next({});
  }

  isKpiLoading(kpiKey: KpiKeys): boolean {
    return this._loadingState.value[kpiKey] || false;
  }
}
